import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription, timer } from 'rxjs';
import { CreatorImport, CreatorImportOptions, CreatorLibraryService } from '../../../services/creator-library.service';
import { SegmentOption } from '../../segmented-control/segmented-control.component';

/** How often a part is tried before the upload gives up (network, 5xx). */
const PART_ATTEMPTS = 3;
const POLL_MS = 2500;
/** Codes that mean "the engine is busy or away": try again later, nothing is wrong with the file. */
const LATER = ['engine_unavailable', 'engine_busy', 'tenant_busy', 'rate_limited', 'engine_timeout', 'upstream_failed'];
/** Which engine limit a document broke (`413 too_large`, `limit.name`) → message. */
const DOCUMENT_LIMITS: Record<string, string> = {
  'documentUpload.fileBytes': 'documentTooBig',
  'documentUpload.lines': 'documentTooManyLines',
  'documentUpload.markdownBytes': 'documentTooMuchText',
};
/** What the importer says about a document it cannot read (`422 import_invalid`, `code`) → message. */
const DOCUMENT_CODES: Record<string, string> = {
  legacy_office: 'legacyOffice',
  encrypted: 'encrypted',
  macro: 'macro',
  pdf_no_text: 'pdfNoText',
  empty: 'empty',
  empty_result: 'emptyResult', // AT: the engine made not a single block of it
};

type DocumentKind = 'elearning_course' | 'microlearning';

/**
 * Creator › start › material zone: an Articulate Rise SCORM package or a
 * document (Word, PowerPoint, PDF, Markdown, text) becomes an E-Learning
 * (al-filemanager `/creator/imports`, plans 4-01 and 4-02,
 * features/docs/technical/creator-items.md „Import"). A document first asks
 * what it should become — a package knows that itself. The file goes up in
 * parts (progress bar), then the engine imports it (Orbi loader with a label,
 * rule 19 C); done, the dialog sums up and opens the course. Closed while the
 * engine works, the import runs on and the course appears in the library.
 * A large document asks first (`needs_confirmation`, Marcel 31.10.2026):
 * the dialog names lessons and blocks, „Trotzdem importieren" lets it run
 * through, closing discards it.
 */
@Component({
  selector: 'app-creator-import-dialog',
  templateUrl: './creator-import-dialog.component.html',
  styleUrl: './creator-import-dialog.component.scss',
})
export class CreatorImportDialogComponent implements OnInit, OnDestroy {
  file!: File;

  state: 'options' | 'uploading' | 'importing' | 'confirm' | 'done' | 'failed' = 'uploading';
  progress = 0;
  result?: CreatorImport;
  errorKey = 'pages.creator.import.errors.failed';
  /** What a document becomes; a Rise package brings its own format. */
  kind: DocumentKind = 'elearning_course';
  kindOptions: SegmentOption[] = [];
  private importId?: string;
  private cancelled = false;
  private poll?: Subscription;

  constructor(
    private ref: NbDialogRef<CreatorImportDialogComponent>,
    private library: CreatorLibraryService,
    private router: Router,
    private toastr: NbToastrService,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    if (this.isDocument) {
      this.kindOptions = [
        { value: 'elearning_course', label: this.t.instant('pages.creator.import.kindCourse') },
        { value: 'microlearning', label: this.t.instant('pages.creator.import.kindMicro') },
      ];
      this.state = 'options';
      return;
    }
    void this.upload({ type: 'rise' });
  }

  ngOnDestroy(): void {
    this.poll?.unsubscribe();
  }

  get fileName(): string {
    return this.file?.name ?? '';
  }

  /** Anything but a .zip is a document (the start page lets only the known endings through). */
  get isDocument(): boolean {
    return !/\.zip$/i.test(this.fileName);
  }

  /** Message keys that differ between a package and a document. */
  key(name: 'title' | 'uploading' | 'importing'): string {
    return `pages.creator.import.${name}${this.isDocument ? 'Document' : ''}`;
  }

  /** „120 Lektionen" or, for Microlearning, „120 Schritte". */
  get largeLessons(): string {
    const summary = this.result?.summary;
    const key = summary?.format === 'microlearning' ? 'largeSteps' : 'largeLessons';
    return this.t.instant(`pages.creator.import.${key}`, { count: summary?.lessons ?? 0 });
  }

  /** The person wants the large document anyway: it runs through. */
  confirmLarge(): void {
    if (!this.importId) return;
    this.state = 'importing';
    this.library.confirmImport(this.importId).subscribe({
      next: () => this.watch(),
      error: (err) => this.fail(err),
    });
  }

  startDocument(): void {
    const lang = ['de', 'en'].includes(this.t.currentLang) ? this.t.currentLang : 'de';
    void this.upload({ type: 'document', kind: this.kind, lang });
  }

  private async upload(options: CreatorImportOptions): Promise<void> {
    this.state = 'uploading';
    try {
      const created = await firstValueFrom(this.library.createImport(this.file, options));
      this.importId = created.importId;
      for (let n = 1; n <= created.parts; n++) {
        if (this.cancelled) return;
        const part = this.file.slice((n - 1) * created.partBytes, n * created.partBytes);
        await this.sendPart(created.importId, n, part);
        this.progress = Math.round((n / created.parts) * 100);
      }
      if (this.cancelled) return;
      await firstValueFrom(this.library.startImport(created.importId));
      this.state = 'importing';
      this.watch();
    } catch (err) {
      if (!this.cancelled) this.fail(err);
    }
  }

  private async sendPart(importId: string, n: number, part: Blob): Promise<void> {
    for (let attempt = 1; ; attempt++) {
      try {
        await firstValueFrom(this.library.importPart(importId, n, part));
        return;
      } catch (err: any) {
        const status = err?.status ?? 0;
        if (attempt >= PART_ATTEMPTS || (status >= 400 && status < 500)) throw err;
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }
    }
  }

  private watch(): void {
    this.poll = timer(POLL_MS, POLL_MS).subscribe(() => {
      if (!this.importId) return;
      this.library.getImport(this.importId).subscribe({
        next: (found) => {
          if (found.status === 'needs_confirmation') {
            this.poll?.unsubscribe();
            this.result = found;
            this.state = 'confirm';
          } else if (found.status === 'done') {
            this.poll?.unsubscribe();
            this.result = found;
            this.state = 'done';
          } else if (found.status === 'failed') {
            this.poll?.unsubscribe();
            this.result = found;
            this.errorKey = this.keyFor(found.errorCode, found.limit?.name, found.code);
            this.state = 'failed';
          }
        },
        error: () => undefined, // a missed poll: the next one asks again
      });
    });
  }

  private fail(err: any): void {
    const body = err?.error ?? {};
    this.errorKey = this.keyFor(body.error, body.limit?.name, body.code);
    this.state = 'failed';
    if (this.importId) this.library.deleteImport(this.importId).subscribe({ error: () => undefined });
  }

  private keyFor(code?: string, limit?: string, importerCode?: string): string {
    const errors = 'pages.creator.import.errors.';
    if (code === 'too_large') {
      if (limit === 'riseUpload.packedBytes') return `${errors}tooBig`;
      if (limit && DOCUMENT_LIMITS[limit]) return `${errors}${DOCUMENT_LIMITS[limit]}`;
      return `${errors}${this.isDocument ? 'documentTooLong' : 'tooMuch'}`;
    }
    if (code === 'import_invalid') {
      if (!this.isDocument) return `${errors}notRise`;
      return `${errors}${DOCUMENT_CODES[importerCode ?? ''] ?? 'notReadable'}`;
    }
    if (code === 'invalid_doc') return `${errors}invalidDoc`;
    if (code === 'forbidden') return `${errors}forbidden`;
    if (code && LATER.includes(code)) return `${errors}later`;
    return `${errors}failed`;
  }

  open(): void {
    const found = this.result;
    if (!found?.itemId) return;
    const title = found.title || this.fileName.replace(/\.[^.]+$/, '');
    this.ref.close();
    this.router.navigate(['/creator/workspace'], { queryParams: { item: found.itemId, kind: found.kind, title } });
  }

  close(): void {
    if (this.state === 'uploading' || this.state === 'confirm') {
      this.cancelled = true;
      if (this.importId) this.library.deleteImport(this.importId).subscribe({ error: () => undefined });
    } else if (this.state === 'importing') {
      this.toastr.info(this.t.instant('pages.creator.import.continues'), '');
    }
    this.ref.close();
  }
}
